import React from 'react';
import { Divider } from '@mui/material';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  AI_COLORS, AI_FONTS,
  ImgSlot, TipBox, CheckItem,
  PageHero, ContentWrap, SectionTitle, SubTitle,
} from '../shared';
import img50 from '../../../imgs/adulto-idosos/50.png';
import img51 from '../../../imgs/adulto-idosos/51.png';

export default function SaudeEmocionalAdulto() {
  const isMobile = useIsMobile();

  const alertas = [
    'Dificuldade para dormir',
    'Cansaço frequente',
    'Irritabilidade',
    'Apertar os dentes (bruxismo)',
  ];

  const quarteto = [
    {
      nome: 'DOPAMINA',
      cor: '#E3F2FD',
      border: AI_COLORS.blue400,
      dicas: ['Durma de 7 a 9 horas', 'Celebre diariamente suas conquistas', 'Exercite-se diariamente'],
    },
    {
      nome: 'OCITOCINA',
      cor: '#EDE7F6',
      border: '#9C27B0',
      dicas: ['Medite todas as manhãs', 'Dê um abraço em alguém', 'Pratique um ato de generosidade'],
    },
    {
      nome: 'SEROTONINA',
      cor: AI_COLORS.teal50,
      border: '#00ACC1',
      dicas: ['Seja grato todos os dias', 'Aproveite a natureza', 'Lembre-se dos momentos importantes'],
    },
    {
      nome: 'ENDORFINA',
      cor: '#FFF8E1',
      border: AI_COLORS.amber300,
      dicas: ['Pratique hobbies', 'Ria com pessoas queridas', 'Dance, cante'],
    },
  ];

  return (
    <div style={{ fontFamily: AI_FONTS.body, background: 'transparent' }}>
      <PageHero
        title="Saúde Emocional"
        subtitle="Cuidar das emoções também é importante. Situações de estresse, ansiedade ou tristeza podem afetar o corpo e a boca."
        audio="saude-emocional.m4a"
      />

      <ContentWrap>

        {/* Alertas */}
        <SectionTitle style={{ marginTop: 0 }}>Fique atento:</SectionTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 12,
            marginBottom: 20,
          }}
        >
          {alertas.map((a, i) => (
            <div
              key={i}
              style={{
                background: AI_COLORS.bgPurple50,
                borderRadius: 10,
                padding: '12px 16px',
                borderLeft: `3px solid #9C27B0`,
                fontWeight: 600,
              }}
            >
              • {a}
            </div>
          ))}
        </div>

        <SubTitle>Dicas para cuidar da saúde emocional:</SubTitle>
        <ul style={{ padding: 0, margin: '0 0 20px' }}>
          <CheckItem>Converse com pessoas de confiança</CheckItem>
          <CheckItem>Faça atividades que goste</CheckItem>
          <CheckItem>Procure ajuda profissional quando necessário</CheckItem>
        </ul>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Conexões saúde emocional e bucal */}
        <SectionTitle>Saúde Emocional e Saúde Bucal</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          A saúde emocional e a saúde bucal estão conectadas. Problemas na boca podem
          afetar o bem-estar psicológico, e o estado emocional pode impactar diretamente a saúde da
          boca.
        </p>

        <div
          style={{
            background: AI_COLORS.bgBlue50,
            borderRadius: 12,
            padding: '18px 22px',
            borderLeft: `4px solid ${AI_COLORS.blue400}`,
          }}
        >
          <SubTitle style={{ marginTop: 0 }}>Onde buscar ajuda?</SubTitle>
          <p style={{ margin: 0, lineHeight: 1.75 }}>
            O Centro de Atenção Psicossocial (CAPS) e as UBSs oferecem serviços de saúde mental
            gratuitos pelo SUS. Conversar com seu médico de família pode ser o primeiro passo para
            acessar suporte emocional adequado.
          </p>
        </div>

        <ImgSlot filename={img50}
          alt="O quarteto da felicidade — dopamina, ocitocina, serotonina e endorfina" />

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Quarteto da felicidade */}
        <SectionTitle>O Quarteto da Felicidade</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          Existem quatro substâncias produzidas pelo nosso organismo que estão diretamente ligadas
          ao bem-estar e à felicidade. Pequenas atitudes do dia a dia podem estimular a produção de
          cada uma delas:
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 16,
            marginBottom: 20,
          }}
        >
          {quarteto.map((q, i) => (
            <div
              key={i}
              style={{
                background: q.cor,
                borderRadius: 14,
                padding: '18px 22px',
                borderTop: `4px solid ${q.border}`,
              }}
            >
              <strong
                style={{
                  fontFamily: AI_FONTS.title,
                  color: AI_COLORS.blue900,
                  display: 'block',
                  marginBottom: 10,
                  fontSize: '1.15rem',
                  letterSpacing: '0.04em',
                }}
              >
                {q.nome}
              </strong>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 2 }}>
                {q.dicas.map((d, j) => (
                  <li key={j} style={{ fontSize: '0.93rem' }}>
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <ImgSlot
          filename={img51}
          alt="O quarteto da felicidade — dopamina, ocitocina, serotonina e endorfina"
          caption="Pequenas atitudes diárias estimulam os hormônios do bem-estar"
        />

        <TipBox>
          Cuidar da saúde emocional é tão importante quanto cuidar da saúde física. Quando as
          emoções estão equilibradas, é mais fácil manter hábitos saudáveis e se sentir bem.
        </TipBox>
      </ContentWrap>
    </div>
  );
}
